import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { BankProfile } from '@otw/models'

interface UseBankProfilesOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
  dontShowUpdates?: boolean
  enabled?: boolean
}

interface CreateBankProfileParams {
  data: Omit<BankProfile, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by'>
}

interface UpdateBankProfileParams {
  id: string
  data: Partial<Omit<BankProfile, 'id' | 'created_at' | 'created_by'>>
}

const normalizeBankContacts = (contacts: BankProfile['bankContacts'] = []) =>
  contacts.map(contact => ({
    ...contact,
    email: contact.email ?? '',
  }))

const normalizeBankProfile = (profile: BankProfile): BankProfile => {
  const profileType = profile.profileType || 'sales'

  return {
    ...profile,
    profileType,
    invoiceInstructions: profile.invoiceInstructions || '',
    bankContacts: normalizeBankContacts(profile.bankContacts || []),
    overnightAddress: profile.overnightAddress || {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US',
    },
    physicalAddress: profile.physicalAddress || {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US',
    },
    email: profile.email || '',
    memo: profile.memo || '',
  }
}

const normalizeBankProfileData = (
  data: Omit<BankProfile, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by'>
) => {
  const profileType = data.profileType || 'sales'

  return {
    ...data,
    profileType,
    invoiceInstructions: data.invoiceInstructions || '',
    bankContacts: normalizeBankContacts(data.bankContacts || []),
    overnightAddress: data.overnightAddress || {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US',
    },
    physicalAddress: data.physicalAddress || {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US',
    },
    email: data.email || '',
    memo: data.memo || '',
  }
}

export const useBankProfiles = (options?: UseBankProfilesOptions) => {
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()
  const { currentUser } = useAuth()

  const queryKey = ['bank_profiles']
  const collectionRef = collection(db, 'bank_profiles')

  const {
    data: bankProfiles,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<BankProfile[]> => {
      const snapshot = await getDocs(collectionRef)
      return snapshot.docs.map(doc => normalizeBankProfile({
        id: doc.id,
        ...doc.data(),
      } as BankProfile))
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled !== false,
  })

  const createBankProfile = useMutation({
    mutationFn: async ({ data }: CreateBankProfileParams) => {
      const now = new Date().toISOString()
      const bankProfileData = {
        ...normalizeBankProfileData(data),
        created_at: now,
        updated_at: now,
        created_by: currentUser?.uid || '',
        updated_by: currentUser?.uid || '',
      }

      const docRef = await addDoc(collectionRef, bankProfileData)
      return { id: docRef.id, ...bankProfileData }
    },
    onSuccess: newProfile => {
      if (!options?.dontShowUpdates) {
        showSuccess('Bank profile created successfully')
      }

      queryClient.setQueryData(queryKey, (old: BankProfile[] | undefined) => {
        return old ? [...old, newProfile] : [newProfile]
      })

      options?.onSuccess?.()
    },
    onError: err => {
      if (!options?.dontShowUpdates) {
        showError('Error creating bank profile')
      }

      console.error('Error creating bank profile:', err)
      options?.onError?.(err as Error)
    },
  })

  const updateBankProfile = useMutation({
    mutationFn: async ({ id, data }: UpdateBankProfileParams) => {
      const docRef = doc(db, 'bank_profiles', id)
      const updateData = {
        ...data,
        ...(data.bankContacts ? { bankContacts: normalizeBankContacts(data.bankContacts) } : {}),
        updated_at: new Date().toISOString(),
        updated_by: currentUser?.uid || '',
      }

      await updateDoc(docRef, updateData)
      return { id, ...updateData }
    },
    onSuccess: updatedProfile => {
      if (!options?.dontShowUpdates) {
        showSuccess('Bank profile updated successfully')
      }

      queryClient.setQueryData(queryKey, (old: BankProfile[] | undefined) => {
        return old
          ? old.map(profile => (profile.id === updatedProfile.id ? { ...profile, ...updatedProfile } : profile))
          : [updatedProfile]
      })

      options?.onSuccess?.()
    },
    onError: err => {
      if (!options?.dontShowUpdates) {
        showError('Error updating bank profile')
      }

      console.error('Error updating bank profile:', err)
      options?.onError?.(err as Error)
    },
  })

  const deleteBankProfile = useMutation({
    mutationFn: async (id: string) => {
      const docRef = doc(db, 'bank_profiles', id)
      await deleteDoc(docRef)
      return id
    },
    onSuccess: (deletedId) => {
      if (!options?.dontShowUpdates) {
        showSuccess('Bank profile deleted successfully')
      }

      queryClient.setQueryData(queryKey, (old: BankProfile[] | undefined) => {
        return old ? old.filter(profile => profile.id !== deletedId) : []
      })

      options?.onSuccess?.()
    },
    onError: err => {
      if (!options?.dontShowUpdates) {
        showError('Error deleting bank profile')
      }

      console.error('Error deleting bank profile:', err)
      options?.onError?.(err as Error)
    },
  })

  return {
    bankProfiles: bankProfiles || [],
    isLoading,
    isError,
    error,
    createBankProfile: createBankProfile.mutate,
    updateBankProfile: updateBankProfile.mutate,
    deleteBankProfile: deleteBankProfile.mutate,
    refetch,
  }
}

